<template>
  <Card cardClasses="shadow-sm border-0">
    <div class="d-flex justify-content-between align-items-center mb-4 flex-wrap gap-2">
      <h4 class="m-0 text-primary">
        <i class="bi bi-award me-2"></i>Brands
      </h4>
      <div class="d-flex gap-2">
        <button class="btn btn-outline-secondary" @click="$router.go(-1)">
          <i class="bi bi-arrow-left me-1"></i> Back
        </button>
        <button v-if="can('create brands')" class="btn btn-primary" @click="openAdd">
          <i class="bi bi-plus-circle me-1"></i> Add Brand
        </button>
      </div>
    </div>

    <div class="d-flex flex-wrap align-items-center justify-content-between mb-3 gap-2">
      <div class="d-flex align-items-center">
        <span class="badge bg-primary rounded-pill me-2">{{ brands.total || 0 }}</span>
        <span class="text-muted">Brands</span>
      </div>
      <form class="d-flex gap-2 align-items-center" @submit.prevent="onSearch">
        <input v-model="search" type="search" class="form-control" placeholder="Search brands">
        <button class="btn btn-primary" type="submit"><i class="bi bi-search"></i></button>
      </form>
    </div>

    <div v-if="loading" class="text-center py-5">
      <Spinner spinner-class="mb-1" />
      <p class="text-muted mt-2">Loading brands...</p>
    </div>

    <div v-else>
      <div v-if="!brands.data.length" class="text-center text-muted py-5">
        <i class="bi bi-award fs-1 d-block mb-2"></i>
        No brands yet. Add brands and attach them to products so customers can shop by brand online.
      </div>
      <div class="row g-3">
        <div v-for="brand in brands.data" :key="brand.id" class="col-xxl-2 col-xl-3 col-md-4 col-sm-6">
          <div class="card h-100 border-0 shadow-sm brand-card">
            <div class="brand-logo-box">
              <img v-if="brand.logo" :src="brand.logo" :alt="brand.name">
              <span v-else class="brand-initials">{{ initials(brand.name) }}</span>
              <span v-if="brand.is_featured" class="badge bg-warning text-dark position-absolute top-0 end-0 m-2">
                <i class="bi bi-star-fill"></i> Featured
              </span>
            </div>
            <div class="card-body p-3">
              <h6 class="mb-1 text-truncate" :title="brand.name">{{ brand.name }}</h6>
              <p class="small text-muted mb-2">{{ brand.products_count }} product{{ brand.products_count === 1 ? '' : 's' }}</p>
              <div class="d-flex gap-2">
                <button v-if="can('edit brands')" class="btn btn-sm btn-outline-primary flex-grow-1" @click="openEdit(brand)">
                  <i class="bi bi-pencil-square"></i> Edit
                </button>
                <button v-if="can('delete brands')" class="btn btn-sm btn-outline-danger" :disabled="brand.products_count > 0"
                        :title="brand.products_count > 0 ? 'Remove the brand from its products first' : 'Delete'"
                        @click="confirmDelete(brand)">
                  <i class="bi bi-trash"></i>
                </button>
              </div>
            </div>
          </div>
        </div>
      </div>

      <div class="d-flex justify-content-end pt-3" v-if="(brands.last_page || 1) > 1">
        <Pagination v-model="page" :pages="brands.last_page" :threshold="2">
          <template #previous>Prev</template>
          <template #next>Next</template>
        </Pagination>
      </div>
    </div>

    <Modal :open="modal" @closed="modal = false" dialog-size="modal-lg">
      <div class="modal-header bg-light">
        <h5 class="modal-title m-0">
          <i class="bi me-2" :class="form.id ? 'bi-pencil-square' : 'bi-plus-circle'"></i>
          {{ form.id ? 'Edit Brand' : 'Add Brand' }}
        </h5>
        <button @click="modal = false" type="button" class="btn-close" aria-label="Close"></button>
      </div>
      <div class="modal-body">
        <div class="row g-4">
          <div class="col-md-4 text-center">
            <div class="brand-logo-box border rounded mb-2">
              <img v-if="form.logo" :src="form.logo" alt="Brand logo">
              <span v-else class="brand-initials">{{ initials(form.name) || '?' }}</span>
            </div>
            <input ref="logoInput" type="file" accept=".png,.jpg,.jpeg,.webp" hidden @change="onLogoSelected">
            <div class="d-flex gap-2">
              <button class="btn btn-outline-primary btn-sm flex-grow-1" type="button" @click="$refs.logoInput.click()">
                <i class="bi bi-upload me-1"></i> {{ form.logo ? 'Change' : 'Upload' }} logo
              </button>
              <button v-if="form.logo" class="btn btn-outline-danger btn-sm" type="button" @click="form.logo = ''">
                <i class="bi bi-x-lg"></i>
              </button>
            </div>
            <small class="text-muted d-block mt-2">Cropped to {{ cropPreset.label }}</small>
          </div>
          <div class="col-md-8">
            <div class="mb-3">
              <label class="form-label fw-bold" for="brand-name">Brand name</label>
              <input id="brand-name" v-model="form.name" type="text" class="form-control" maxlength="100" placeholder="e.g. Coca-Cola">
            </div>
            <div class="mb-3">
              <label class="form-label fw-bold" for="brand-description">Description <span class="text-muted small fw-normal">(optional)</span></label>
              <textarea id="brand-description" v-model="form.description" class="form-control" rows="3" maxlength="255"></textarea>
            </div>
            <div class="form-check form-switch">
              <input id="brand-featured" v-model="form.is_featured" class="form-check-input" type="checkbox">
              <label class="form-check-label" for="brand-featured">Featured brand (shown first in the online shop)</label>
            </div>
          </div>
        </div>
      </div>
      <div class="modal-footer bg-light">
        <button type="button" class="btn btn-outline-secondary" @click="modal = false">Cancel</button>
        <button type="button" class="btn btn-primary" :disabled="saving || !form.name" @click="save">
          <span v-if="saving" class="spinner-border spinner-border-sm me-1"></span>
          {{ form.id ? 'Update Brand' : 'Create Brand' }}
        </button>
      </div>
    </Modal>

    <ImageCropper
      :open="cropper.open"
      :file="cropper.file"
      :aspect-ratio="cropPreset.aspectRatio"
      :output-width="cropPreset.width"
      :output-height="cropPreset.height"
      :output-type="cropPreset.type"
      title="Crop brand logo"
      @cropped="dataUrl => form.logo = dataUrl"
      @closed="cropper.open = false"
    />
  </Card>
</template>

<script>
import brandService from "@/api/brandService";
import confirmDialog from "@/utils/confirmDialog";
import permissions from "@/utils/permissions";
import { decrypt } from "@/utils/crypto";
import { CROP_PRESETS } from "@/utils/cropPresets";

const emptyForm = () => ({ id: null, name: '', description: '', is_featured: false, logo: '' })

export default {
  name: "Brands",

  data() {
    return {
      loading: true,
      saving: false,
      brands: { data: [] },
      page: 1,
      search: '',
      modal: false,
      form: emptyForm(),
      profile: {},
      userPermissions: [],
      cropper: { open: false, file: null },
      cropPreset: CROP_PRESETS.logo,
    }
  },

  methods: {
    can(permission) {
      if (['primary', 'super-admin'].includes(this.profile.role)) return true
      return permissions.hasPermission(this.userPermissions, permission)
    },
    initials(name) {
      return (name || '').split(/\s+/).filter(Boolean).slice(0, 2).map(w => w[0].toUpperCase()).join('')
    },
    async getBrands() {
      this.loading = true
      try {
        const response = await brandService.getBrands({ page: this.page, per_page: 24, search: this.search.trim() || undefined })
        this.brands = response && response.data ? response : { data: [] }
      } finally {
        this.loading = false
      }
    },
    onSearch() {
      if (this.page !== 1) this.page = 1
      else this.getBrands()
    },
    openAdd() {
      this.form = emptyForm()
      this.modal = true
    },
    openEdit(brand) {
      this.form = {
        id: brand.id,
        name: brand.name,
        description: brand.description || '',
        is_featured: !!brand.is_featured,
        logo: brand.logo || '',
      }
      this.modal = true
    },
    onLogoSelected(event) {
      const [file] = event.target.files || []
      if (file) {
        this.cropper.file = file
        this.cropper.open = true
      }
      event.target.value = ''
    },
    async save() {
      this.saving = true
      try {
        const payload = {
          name: this.form.name,
          description: this.form.description || null,
          is_featured: this.form.is_featured,
          logo: this.form.logo || null,
        }
        const result = this.form.id
          ? await brandService.updateBrand(payload, this.form.id)
          : await brandService.createBrand(payload)
        if (result) {
          this.modal = false
          await this.getBrands()
        }
      } finally {
        this.saving = false
      }
    },
    confirmDelete(brand) {
      confirmDialog('Brand', async () => {
        if (await brandService.deleteBrand(brand.id)) await this.getBrands()
      }, `Are you sure you want to delete "${brand.name}"?`)
    },
  },

  watch: {
    page() {
      this.getBrands()
    },
  },

  async created() {
    this.profile = JSON.parse(decrypt(sessionStorage.getItem('profile')))
    this.userPermissions = await permissions.getUserPermissions(this.profile.id)
    await this.getBrands()
  },
}
</script>

<style scoped>
.brand-card {
  border-radius: 12px;
  overflow: hidden;
  transition: transform .2s ease, box-shadow .2s ease;
}

.brand-card:hover {
  transform: translateY(-3px);
  box-shadow: 0 8px 18px rgba(0, 0, 0, .08) !important;
}

.brand-logo-box {
  position: relative;
  aspect-ratio: 1 / 1;
  display: flex;
  align-items: center;
  justify-content: center;
  background: #f8f9fa;
  overflow: hidden;
}

.brand-logo-box img {
  width: 100%;
  height: 100%;
  object-fit: contain;
  padding: 12px;
}

.brand-initials {
  font-size: 2rem;
  font-weight: 700;
  color: #adb5bd;
}
</style>
